import React, { useState } from 'react';
import { Button, Badge } from '../../../components/UI';
import { IconCaretDown, IconCaretUp } from '../../../components/Icon';
import { formatMatchCount } from '../utils/formatters';
import { DocumentHit } from '../types';
import { HydratedSnippet, postHydrateEmbeddings } from '../../../api/signalSearchApi';
import HydratedSnippetView from './HydratedSnippetView';
import styles from './DocumentHitList.module.scss';
import { mergeHydratedSnippets } from '../../../store/search/slice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { openDocument } from '../../../store/document/slice';
import { trackEvent } from '../../../services/tracking';

interface DocumentHitListProps {
  documents: DocumentHit[];
  isCompanyExpanding: boolean;
}

const DocumentHitList: React.FC<DocumentHitListProps> = ({ documents, isCompanyExpanding }) => {
  const dispatch = useAppDispatch();
  const [loadingDocIds, setLoadingDocIds] = useState<Record<string, boolean>>({});
  const hydratedById = useAppSelector((s) => s.signalSearch.hydratedById);
  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});

  const hydrateEmbeddings = async (embeddingIds: string[], docId?: string) => {
    if (docId) setLoadingDocIds((m) => ({ ...m, [docId]: true }));
    try {
      const hydrated = await postHydrateEmbeddings({ embeddingIds });
      dispatch(mergeHydratedSnippets(Object.fromEntries(hydrated.map((h) => [h.embeddingId, h]))));
    } finally {
      if (docId) setLoadingDocIds((m) => ({ ...m, [docId]: false }));
    }
  };
  const onOpenSnippet = (documentId: string, pageId: string, textToHighlight: string) => {
    trackEvent('Citation Opened', {
      location: 'SignalSearch',
      documentId,
      pageId,
    });
    dispatch(openDocument({ documentId, pageId, textToHighlight }));
  };

  return (
    <div className={styles.list}>
      {documents.map((doc) => {
        const hydratedEntries: { snippet: HydratedSnippet; similarity: number }[] = [];
        const remainingIds: string[] = [];
        for (const e of doc.embeddings) {
          const s = hydratedById[e.embeddingId];
          if (s) {
            hydratedEntries.push({
              snippet: s,
              similarity: e.similarity,
            });
          } else {
            remainingIds.push(e.embeddingId);
          }
        }

        const isExpanded = !!expandedDocs[doc.documentId];
        const canToggle = hydratedEntries.length > 1 || remainingIds.length > 0;

        return (
          <div key={doc.documentId} className={styles.docCard}>
            <div className={styles.docHeader}>
              <div>
                <strong>{doc.documentName}</strong>
              </div>
              <div className={styles.docHeaderRight}>
                <Badge variant="neutral">{formatMatchCount(doc.matchCount)}</Badge>
                {canToggle ? (
                  <Button
                    variant="ghost"
                    size="small"
                    loading={!!loadingDocIds[doc.documentId] || isCompanyExpanding}
                    onClick={async () => {
                      trackEvent('Document Snippets Toggle', {
                        documentId: doc.documentId,
                        expanded: !isExpanded,
                      });
                      if (isExpanded) {
                        setExpandedDocs((m) => ({ ...m, [doc.documentId]: false }));
                        return;
                      }
                      if (remainingIds.length > 0) {
                        await hydrateEmbeddings(remainingIds, doc.documentId);
                      }
                      setExpandedDocs((m) => ({ ...m, [doc.documentId]: true }));
                    }}
                    aria-label={isExpanded ? 'Collapse snippets' : 'Load more snippets'}
                  >
                    {isExpanded ? <IconCaretUp /> : <IconCaretDown />}
                  </Button>
                ) : (
                  <span className={styles.caretSlot} />
                )}
              </div>
            </div>

            {hydratedEntries.length > 0 && (
              <div className={styles.snippetList}>
                {(isExpanded ? hydratedEntries : hydratedEntries.slice(0, 1)).map(
                  ({ snippet, similarity }) => (
                    <div key={snippet.embeddingId} className={styles.snippetList}>
                      <div className={styles.snippetHeader}>
                        <Badge variant="success">{(similarity * 100).toFixed(1)}%</Badge>
                        <Button
                          variant="outline"
                          size="small"
                          onClick={() =>
                            onOpenSnippet(doc.documentId, snippet.pageId, snippet.highlightText)
                          }
                        >
                          View in Document
                        </Button>
                      </div>
                      <HydratedSnippetView snippet={snippet} />
                    </div>
                  ),
                )}
              </div>
            )}

            {null}
          </div>
        );
      })}
    </div>
  );
};

export default DocumentHitList;
